// Phase 3.6 (beads hh-0rw) — CatalogEquipmentScreen.
//
// case-24 flow: pick one of the 8 catalog equipment types → describe it
// (name + optional photo stub) → the screen proposes the type's default
// maintenance schedule → confirm in one action. On confirm:
//   POST /api/v1/equipment            → create the equipment
//   POST /api/v1/equipment/{id}/schedule  (once per CHECKED rule)
// then navigate to the new eq_card.
//
// Ported (style + flow) from designproject/files/screens-onboard.jsx
// (`CatalogEquipmentScreen`). The design used a static catalog; here the
// catalog comes from GET /api/v1/catalog/equipment.
//
// SAFETY MARKER (aidepo-18/42) — every proposed schedule rule gets its own
// checkbox in the proposal phase. Safety-critical rules carry a "safety" chip
// and are PRE-CHECKED by default (recommended), but stay fully toggleable: the
// user may opt out of ANY rule, including safety. Only the checked rules are
// persisted on confirm — safety is a recommendation, never a hard block.

import type { JSX } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { T, Icon, Chip, Avatar } from '../../tokens';
import { ScreenHeader } from '../../shell';
import type { RouterApi } from '../../router';
import { catalog, equipment, properties, schedule, humanizeError } from '../../api';
import type { CatalogEntry } from '../../api/endpoints/catalog.js';
import type { PropertyTreeNode } from '../../api/endpoints/properties.js';
import { FormLabel, FormInput, FormFooter, FormError } from '../forms/formPrimitives';
import { EquipmentPlacePicker } from '../forms/EquipmentPlacePicker';
import type { EquipmentPlaceSelection } from '../forms/EquipmentPlacePicker';

export interface CatalogEquipmentScreenProps {
  readonly router: RouterApi;
}

type Phase = 'intro' | 'describe' | 'proposal';
type CatalogStatus = 'loading' | 'ready' | 'error';
type LoadStatus = 'loading' | 'ready' | 'error';

// Equipment requires a location — the backend rejects a request with neither
// room nor property (422, CHECK constraint ck_equipment_location). The shared
// EquipmentPlacePicker lets the user pick a room or a whole property; when the
// home has no properties at all, a default one is bootstrapped on confirm.

function describeError(err: unknown): string {
  return humanizeError(err, 'save');
}

function periodLabel(months: number): string {
  if (months % 12 === 0) {
    const years = months / 12;
    return years === 1 ? '1 год' : `${years} года`;
  }
  return `${months} мес.`;
}

export function CatalogEquipmentScreen({ router }: CatalogEquipmentScreenProps): JSX.Element {
  const [catStatus, setCatStatus] = useState<CatalogStatus>('loading');
  const [entries, setEntries] = useState<readonly CatalogEntry[]>([]);
  const [catError, setCatError] = useState<string | null>(null);

  const [locStatus, setLocStatus] = useState<LoadStatus>('loading');
  const [propertyTree, setPropertyTree] = useState<readonly PropertyTreeNode[]>([]);
  const [locError, setLocError] = useState<string | null>(null);
  const [selectedLoc, setSelectedLoc] = useState<EquipmentPlaceSelection | null>(null);

  const [phase, setPhase] = useState<Phase>('intro');
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [name, setName] = useState<string>('');
  const [pending, setPending] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Which proposed maintenance rules will be persisted, keyed by rule title.
  // The user may opt out of ANY rule, including safety (aidepo-18/42); we seed
  // to "all selected" whenever the catalog type changes (effect below).
  const [checkedRules, setCheckedRules] = useState<ReadonlySet<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    setCatStatus('loading');
    catalog
      .getEquipmentCatalog()
      .then((res) => {
        if (cancelled) return;
        setEntries(res.equipment);
        setCatStatus('ready');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setCatError(humanizeError(err, 'load'));
        setCatStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLocStatus('loading');
    properties
      .getPropertyTree()
      .then((tree) => {
        if (cancelled) return;
        setPropertyTree(tree.properties);
        setLocStatus('ready');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLocError(humanizeError(err, 'load'));
        setLocStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const selected = useMemo<CatalogEntry | null>(
    () => entries.find((e) => e.type === selectedType) ?? null,
    [entries, selectedType],
  );

  // Seed the rule selection to "all checked" whenever the chosen catalog type
  // changes. Safety rules are included in the default set — they are recommended
  // and pre-checked, but the user may uncheck any of them (aidepo-18/42).
  useEffect(() => {
    setCheckedRules(new Set((selected?.rules ?? []).map((r) => r.title)));
  }, [selected]);

  const toggleRule = (title: string): void => {
    // Every rule is toggleable, including safety — it is a marker, not a lock.
    if (pending) return;
    setCheckedRules((set) => {
      const next = new Set(set);
      if (next.has(title)) next.delete(title);
      else next.add(title);
      return next;
    });
  };

  const pickType = (entry: CatalogEntry): void => {
    setSelectedType(entry.type);
    setName(entry.displayName);
    setError(null);
    setPhase('describe');
  };

  const back = (): void => {
    if (phase === 'intro') {
      router.back();
      return;
    }
    if (phase === 'proposal') {
      setPhase('describe');
      return;
    }
    setPhase('intro');
  };

  const confirm = (): void => {
    if (selected === null || name.trim().length === 0) return;
    // Equipment must have a location. When the home has no properties yet,
    // bootstrap a default one so the wizard still completes in one action.
    if (selectedLoc === null && propertyTree.length > 0) {
      setPhase('describe');
      setError('Выбери место для оборудования.');
      return;
    }
    setPending(true);
    setError(null);
    const locate = async (): Promise<{ roomId?: string; propertyId?: string }> => {
      if (selectedLoc?.kind === 'room') return { roomId: selectedLoc.id };
      if (selectedLoc?.kind === 'property') return { propertyId: selectedLoc.id };
      const created = await properties.createProperty({ name: 'Мой дом' });
      return { propertyId: created.id };
    };
    locate()
      .then((location) =>
        equipment.addEquipment({
          name: name.trim(),
          category: selected.category,
          ...location,
        }),
      )
      .then(async (created) => {
        // Persist ONLY the rules the user kept checked (aidepo-18/42 — any rule,
        // including safety, may be opted out). When nothing is checked the
        // equipment is still created, just without a schedule. Sequential keeps
        // change-record order deterministic and surfaces the first failure cleanly.
        for (const rule of selected.rules) {
          if (!checkedRules.has(rule.title)) continue;
          await schedule.addScheduleRule(created.id, {
            title: rule.title,
            periodMonths: rule.periodMonths,
            isSafetyCritical: rule.isSafetyCritical,
            fromCatalog: true,
          });
        }
        router.go('eq_card', { id: created.id });
      })
      .catch((err: unknown) => {
        setError(describeError(err));
        setPending(false);
      });
  };

  const headerTitle =
    phase === 'intro'
      ? 'Добавить оборудование'
      : phase === 'describe'
        ? 'Что добавляем'
        : 'Готовый регламент';
  const headerSubtitle =
    phase === 'intro'
      ? 'Выбери тип — я подготовлю график ТО'
      : phase === 'describe'
        ? 'Уточни название и фото'
        : selected !== null
          ? `Узнал: ${selected.displayName.toLowerCase()}`
          : '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: T.bg }}>
      <ScreenHeader title={headerTitle} subtitle={headerSubtitle} onBack={back} />

      <div style={{ flex: 1, overflow: 'auto', padding: '4px 16px 16px' }}>
        {phase === 'intro' && (
          <>
            {catStatus === 'loading' && (
              <div style={{ fontSize: 12.5, color: T.ink3, padding: '6px 2px' }}>Загружаю каталог…</div>
            )}
            {catStatus === 'error' && catError !== null && <FormError message={catError} />}
            {catStatus === 'ready' && (
              <>
                <FormLabel style={{ padding: '6px 4px 8px' }}>Из каталога</FormLabel>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {entries.map((entry) => {
                    const hasSafety = entry.rules.some((r) => r.isSafetyCritical);
                    return (
                      <button
                        key={entry.type}
                        type="button"
                        onClick={() => pickType(entry)}
                        style={{
                          padding: 12,
                          background: T.surface,
                          border: `1px solid ${T.border}`,
                          borderRadius: 12,
                          fontFamily: T.font,
                          cursor: 'pointer',
                          textAlign: 'left',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 8,
                          minHeight: 92,
                        }}
                      >
                        <Icon
                          name={hasSafety ? 'shield' : 'sliders'}
                          size={22}
                          color={hasSafety ? T.safety : T.ink2}
                        />
                        <div>
                          <div
                            style={{
                              fontSize: 13.5,
                              fontWeight: 700,
                              color: T.ink,
                              lineHeight: 1.2,
                              display: 'flex',
                              alignItems: 'center',
                              gap: 6,
                              flexWrap: 'wrap',
                            }}
                          >
                            {entry.displayName}
                            {hasSafety && <Chip tone="safety">safety</Chip>}
                          </div>
                          <div style={{ fontSize: 11.5, color: T.ink3, marginTop: 3, lineHeight: 1.3 }}>
                            {entry.rules.length === 0
                              ? 'без регламента'
                              : `${entry.rules.length} пункта ТО`}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>

                <FormLabel style={{ padding: '18px 4px 8px' }}>Нет в каталоге</FormLabel>
                <button
                  type="button"
                  onClick={() => router.go('out_of_catalog')}
                  style={{
                    width: '100%',
                    padding: 14,
                    background: T.surface,
                    border: `1.5px dashed ${T.borderStrong}`,
                    borderRadius: 14,
                    fontFamily: T.font,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    textAlign: 'left',
                  }}
                >
                  <Avatar kind="ai" icon="spark" size={36} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: T.ink }}>
                      Другое оборудование
                    </div>
                    <div style={{ fontSize: 12, color: T.ink3, marginTop: 2, lineHeight: 1.3 }}>
                      Задам пару вопросов и соберу черновую карточку с графиком ТО
                    </div>
                  </div>
                  <Icon name="chevR" size={18} color={T.ink3} />
                </button>
              </>
            )}
          </>
        )}

        {phase === 'describe' && selected !== null && (
          <>
            <FormLabel>Название</FormLabel>
            <FormInput
              value={name}
              onChange={setName}
              placeholder="Например: котёл на кухне"
              disabled={pending}
            />

            <FormLabel style={{ marginTop: 18 }}>Где находится</FormLabel>
            {locStatus === 'loading' && (
              <div style={{ fontSize: 12.5, color: T.ink3, padding: '6px 2px' }}>
                Загружаю помещения…
              </div>
            )}
            {locStatus === 'error' && locError !== null && <FormError message={locError} />}
            {locStatus === 'ready' && propertyTree.length === 0 && (
              <div style={{ fontSize: 12.5, color: T.ink3, padding: '6px 2px', lineHeight: 1.4 }}>
                Помещений пока нет — оборудование попадёт в дом «Мой дом», он создастся
                автоматически.
              </div>
            )}
            {locStatus === 'ready' && propertyTree.length > 0 && (
              <EquipmentPlacePicker
                tree={propertyTree}
                value={selectedLoc}
                onChange={(sel) => {
                  setSelectedLoc(sel);
                  setError(null);
                }}
                disabled={pending}
              />
            )}

            <FormLabel style={{ marginTop: 18 }}>Фото · необязательно</FormLabel>
            <button
              type="button"
              disabled
              title="Скоро"
              style={{
                width: '100%',
                padding: 14,
                background: T.surface,
                border: `1.5px dashed ${T.borderStrong}`,
                borderRadius: 14,
                fontFamily: T.font,
                cursor: 'not-allowed',
                opacity: 0.7,
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                textAlign: 'left',
              }}
            >
              <Avatar kind="sys" icon="camera" size={36} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.ink }}>Снять оборудование</div>
                <div style={{ fontSize: 12, color: T.ink3, marginTop: 2 }}>скоро</div>
              </div>
            </button>

            {error !== null && <FormError message={error} />}
          </>
        )}

        {phase === 'proposal' && selected !== null && (
          <>
            <div
              style={{
                background: T.brandSoft,
                border: `1px solid ${T.brandSoft2}`,
                borderRadius: 14,
                padding: 14,
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}
            >
              <Avatar kind="ai" icon="check" size={36} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: T.brandDeep }}>
                  Типовой регламент готов
                </div>
                <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 2 }}>
                  Выбрано {checkedRules.size} из {selected.rules.length} ·{' '}
                  {selected.rules.filter((r) => r.isSafetyCritical).length} safety-критичных
                </div>
              </div>
            </div>

            <FormLabel style={{ marginTop: 18 }}>Регламент ТО · отметь нужное</FormLabel>
            {selected.rules.length === 0 ? (
              <div
                style={{
                  padding: '16px 14px',
                  background: T.surfaceAlt,
                  border: `1px solid ${T.border}`,
                  borderRadius: 14,
                  fontSize: 13,
                  color: T.ink3,
                  textAlign: 'center',
                  fontWeight: 500,
                }}
              >
                Для этого типа регламента нет — оборудование добавится без графика ТО.
              </div>
            ) : (
              <div
                style={{
                  background: T.surface,
                  border: `1px solid ${T.border}`,
                  borderRadius: 14,
                  overflow: 'hidden',
                }}
              >
                {selected.rules.map((rule, i) => (
                  <div key={rule.title}>
                    {i > 0 && <div style={{ height: 1, background: T.divider, marginLeft: 46 }} />}
                    <RuleRow
                      title={rule.title}
                      periodLabel={periodLabel(rule.periodMonths)}
                      isSafetyCritical={rule.isSafetyCritical}
                      checked={checkedRules.has(rule.title)}
                      disabled={pending}
                      onToggle={() => toggleRule(rule.title)}
                    />
                  </div>
                ))}
              </div>
            )}
            {selected.rules.some((r) => r.isSafetyCritical) && (
              <div
                style={{
                  marginTop: 12,
                  padding: 12,
                  background: T.safetySoft,
                  border: '1px solid #E8D7A8',
                  borderRadius: 12,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}
              >
                <Icon name="shield" size={16} color={T.safety} />
                <div style={{ fontSize: 12.5, color: T.safety, fontWeight: 600, flex: 1, lineHeight: 1.4 }}>
                  Safety-пункты отмечены и предложены по умолчанию — это важные
                  проверки. Снять отметку можно, решение за вами.
                </div>
              </div>
            )}

            {error !== null && <FormError message={error} />}
          </>
        )}
      </div>

      {phase === 'describe' && (
        <FormFooter
          onCancel={() => setPhase('intro')}
          onConfirm={() => setPhase('proposal')}
          confirmLabel="Дальше"
          confirmIcon="chevR"
          disabled={
            name.trim().length === 0 ||
            pending ||
            locStatus !== 'ready' ||
            (propertyTree.length > 0 && selectedLoc === null)
          }
        />
      )}
      {phase === 'proposal' && (
        <FormFooter
          onCancel={() => setPhase('describe')}
          onConfirm={confirm}
          confirmLabel="Подтвердить"
          confirmIcon="check"
          disabled={pending}
          pending={pending}
        />
      )}
    </div>
  );
}

interface RuleRowProps {
  readonly title: string;
  readonly periodLabel: string;
  readonly isSafetyCritical: boolean;
  readonly checked: boolean;
  readonly disabled: boolean;
  readonly onToggle: () => void;
}

/**
 * One toggleable maintenance rule in the proposal preview. Mirrors the
 * checkbox-row pattern from RegulationDiffScreen's DiffRow: a tappable row with
 * a checkbox box, title, period, and an optional "safety" chip. Safety rules
 * are pre-checked elsewhere but remain fully toggleable here (aidepo-18/42).
 */
function RuleRow({
  title,
  periodLabel,
  isSafetyCritical,
  checked,
  disabled,
  onToggle,
}: RuleRowProps): JSX.Element {
  return (
    <div
      onClick={disabled ? undefined : onToggle}
      style={{
        padding: '12px 14px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      <div
        role="checkbox"
        aria-checked={checked}
        aria-disabled={disabled}
        style={{
          width: 22,
          height: 22,
          borderRadius: 6,
          marginTop: 1,
          flexShrink: 0,
          border: `2px solid ${checked ? T.brand : T.borderStrong}`,
          background: checked ? T.brand : 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {checked && <Icon name="check" size={13} color="#fff" strokeWidth={2.5} />}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: T.ink }}>{title}</span>
          {isSafetyCritical && (
            <Chip tone="safety" icon="shield">
              safety
            </Chip>
          )}
        </div>
        <div style={{ fontSize: 12, color: T.ink3, marginTop: 4 }}>Период {periodLabel}</div>
      </div>
    </div>
  );
}
